import { ArrowRight, Sparkle } from 'lucide-react';
import { FadeIn } from './Reveal';
import { PhoneMockup } from './PhoneMockup';
import { useLang } from '../i18n/LangContext';

export const IskraSection = () => {
  const { t } = useLang();
  const ISKRA_FEATURES = t.iskra.features;
  return (
  <section id="iskra" data-testid="iskra-section" className="relative bg-iskra-bg py-24 md:py-32 overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(217,70,239,0.22),_transparent_55%)]" aria-hidden="true" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(236,72,153,0.16),_transparent_50%)]" aria-hidden="true" />
    {[['12%', '18%'], ['78%', '12%'], ['65%', '70%'], ['22%', '78%'], ['88%', '45%']].map(([left, top], i) => (
      <Sparkle
        key={i}
        size={i % 2 === 0 ? 16 : 11}
        className="absolute text-iskra-magenta/60 animate-sparkle-pulse"
        style={{ left, top, animationDelay: `${i * 0.7}s` }}
        aria-hidden="true"
      />
    ))}

    <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
      <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-20 items-center">
        <div>
          <FadeIn>
            <p data-testid="iskra-eyebrow" className="font-heading text-xs md:text-sm font-bold tracking-[0.3em] uppercase text-iskra-magenta mb-5">
              <span className="text-iskra-pink mr-3">06</span>
              {t.iskra.eyebrow}
            </p>
            <h2 data-testid="iskra-title" className="font-heading font-extrabold tracking-tighter text-[clamp(2.75rem,6vw,4.75rem)] leading-[1] text-gradient-iskra">
              KRUG ISKRA
            </h2>
            <p className="mt-5 font-heading text-xl md:text-2xl font-bold text-white">
              {t.iskra.sub}
            </p>
            <p className="mt-4 text-base md:text-lg text-fuchsia-100/60 leading-relaxed max-w-xl">
              {t.iskra.copy}
            </p>
          </FadeIn>

          <div className="mt-11 grid sm:grid-cols-2 gap-4">
            {ISKRA_FEATURES.map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.07}>
                <article
                  data-testid={`iskra-feature-${i}`}
                  className="group h-full rounded-2xl border border-iskra-magenta/20 bg-white/[0.04] backdrop-blur-sm p-6 hover:border-iskra-magenta/60 hover:bg-iskra-magenta/[0.07] transition-colors duration-500"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-iskra-magenta/15 text-iskra-magenta group-hover:text-iskra-pink transition-colors duration-300">
                    <f.icon size={20} />
                  </span>
                  <h3 className="mt-4 font-heading text-base md:text-lg font-extrabold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-fuchsia-100/55 leading-relaxed">{f.copy}</p>
                </article>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.3}>
            <p data-testid="iskra-statement" className="mt-11 font-heading text-2xl md:text-3xl font-extrabold text-white">
              {t.iskra.statementA}<span className="text-gradient-iskra">{t.iskra.statementB}</span>
            </p>
            <a
              href="#waitlist"
              data-testid="iskra-cta"
              className="group mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-iskra-magenta to-iskra-pink px-7 py-3.5 font-heading font-bold text-white shadow-[0_18px_50px_-12px_rgba(217,70,239,0.6)] hover:shadow-[0_22px_60px_-10px_rgba(236,72,153,0.7)] transition-shadow duration-300"
            >
              {t.iskra.cta}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </FadeIn>
        </div>

        <FadeIn delay={0.15} className="relative flex justify-center">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] h-[440px] rounded-full bg-iskra-magenta/25 blur-[120px]" aria-hidden="true" />
          <div className="relative w-[250px] md:w-[290px] animate-float-soft" style={{ animationDelay: '0.8s' }}>
            <PhoneMockup src="/assets/screen-iskra.webp" alt="KRUG ISKRA — današnji krug i alati za upoznavanje" glow="iskra" />
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
  );
};
